import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { startAIGeneration, startMermaidImport, importDiagramFromFile } from '../../lib/editorActions';
import {
  Search,
  Plus,
  Sparkles,
  Upload,
  Workflow,
  Home,
  LayoutTemplate,
  Settings,
  CreditCard,
} from '@/components/icons';
import { DiagramIcon } from '../diagramIcons';
import { useDiagramsQuery } from '../../hooks/queries';
import { useAuth } from '../../pages/Auth/AuthProvider';
import { useTabs } from '../../context/TabsContext';

interface CommandMenuContextValue {
  open: () => void;
}
const CommandMenuContext = createContext<CommandMenuContextValue | undefined>(undefined);

export function useCommandMenu() {
  const ctx = useContext(CommandMenuContext);
  if (!ctx) throw new Error('useCommandMenu must be used within a CommandMenuProvider');
  return ctx;
}

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon: React.ReactNode;
  run: () => void;
}

/**
 * Global ⌘K command palette for the workspace: search diagrams, jump to pages,
 * run quick actions. No open/close animation — it's a high-frequency action
 * (Raycast-style instant), per Emil's framework.
 */
export function CommandMenuProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { data: diagrams = [] } = useDiagramsQuery(isAuthenticated);
  const { openTab } = useTabs();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setActive(0);
  }, []);

  // ⌘K / Ctrl+K toggles the palette — except in the editor and demo editor,
  // which own ⌘K for their own node/icon palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (
          location.pathname.startsWith('/editor') ||
          location.pathname.startsWith('/demo')
        )
          return;
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const go = useCallback(
    (path: string) => {
      close();
      navigate(path);
    },
    [close, navigate]
  );

  const items = useMemo<CommandItem[]>(() => {
    const iconCls = 'h-4 w-4 flex-none text-muted-foreground';
    const actions: CommandItem[] = [
      { id: 'new', label: 'New diagram', group: 'Actions', icon: <Plus className={iconCls} />, run: () => go('/editor') },
      { id: 'ai', label: 'Generate with AI', group: 'Actions', icon: <Sparkles className={iconCls} />, run: () => startAIGeneration(go) },
      { id: 'import', label: 'Import diagram', group: 'Actions', icon: <Upload className={iconCls} />, run: () => { close(); importDiagramFromFile(navigate); } },
      { id: 'import-mermaid', label: 'Import Mermaid', group: 'Actions', icon: <Workflow className={iconCls} />, run: () => { close(); startMermaidImport(navigate); } },
    ];
    const nav: CommandItem[] = [
      { id: 'home', label: 'Home', group: 'Go to', icon: <Home className={iconCls} />, run: () => go('/') },
      { id: 'templates', label: 'Templates', group: 'Go to', icon: <LayoutTemplate className={iconCls} />, run: () => go('/templates') },
      { id: 'settings', label: 'Settings', group: 'Go to', icon: <Settings className={iconCls} />, run: () => go('/settings') },
      { id: 'billing', label: 'Billing', group: 'Go to', icon: <CreditCard className={iconCls} />, run: () => go('/billing') },
    ];
    const recent = [...diagrams]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 20)
      .map<CommandItem>((d) => ({
        id: `d-${d.id}`,
        label: d.name,
        hint: `${d.nodeCount} nodes`,
        group: 'Diagrams',
        icon: <DiagramIcon name={d.icon} color={d.iconColor} className="h-4 w-4 flex-none" />,
        run: () => {
          openTab({ id: d.id, name: d.name, icon: d.icon, iconColor: d.iconColor });
          close();
          navigate(`/editor/${d.id}`);
        },
      }));
    return [...actions, ...nav, ...recent];
  }, [diagrams, go, openTab, close, navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.label.toLowerCase().includes(q) || i.group.toLowerCase().includes(q));
  }, [query, items]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  // Keep the active item visible during keyboard navigation.
  useEffect(() => {
    if (!isOpen) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, isOpen]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (filtered.length ? (a + 1) % filtered.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (filtered.length ? (a - 1 + filtered.length) % filtered.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      filtered[active]?.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  return (
    <CommandMenuContext.Provider value={{ open }}>
      {children}
      {isOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 px-4 pt-[12vh]"
          onClick={close}
          role="presentation"
        >
          <div
            className="flex w-full max-w-[600px] flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Command menu"
          >
            <div className="flex items-center gap-2.5 border-b border-border px-4">
              <Search className="h-4 w-4 flex-none text-muted-foreground" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search diagrams, go to a page, or run an action…"
                className="h-12 w-full bg-transparent text-[14px] outline-none placeholder:text-muted-foreground"
              />
              <kbd className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">esc</kbd>
            </div>

            <div ref={listRef} className="max-h-[380px] overflow-y-auto p-1.5">
              {filtered.length === 0 ? (
                <div className="px-3 py-10 text-center text-[13px] text-muted-foreground">
                  No results for “{query}”
                </div>
              ) : (
                filtered.map((item, i) => {
                  const showLabel = i === 0 || filtered[i - 1].group !== item.group;
                  const isActive = i === active;
                  return (
                    <React.Fragment key={item.id}>
                      {showLabel && (
                        <div className="px-2.5 pb-1 pt-2.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                          {item.group}
                        </div>
                      )}
                      <button
                        type="button"
                        data-index={i}
                        onMouseMove={() => setActive(i)}
                        onClick={() => item.run()}
                        className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] ${
                          isActive ? 'bg-muted text-foreground' : 'text-foreground'
                        }`}
                      >
                        {item.icon}
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.hint && <span className="font-mono text-[11px] text-muted-foreground">{item.hint}</span>}
                        {isActive && <span className="font-mono text-[11px] text-muted-foreground">↵</span>}
                      </button>
                    </React.Fragment>
                  );
                })
              )}
            </div>

            <div className="flex items-center gap-3 border-t border-border px-3.5 py-2 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><kbd className="font-mono">↑↓</kbd> navigate</span>
              <span className="flex items-center gap-1"><kbd className="font-mono">↵</kbd> select</span>
              <span className="flex items-center gap-1"><kbd className="font-mono">esc</kbd> close</span>
            </div>
          </div>
        </div>
      )}
    </CommandMenuContext.Provider>
  );
}
